/**
 * Proximity-based Link Analysis Utilities
 * Finds connections between devices based on geographic proximity
 */

import { PathfinderDataPoint } from '../services/pathfinderApi';
import { batchReverseGeocodeRich } from './geocoding';

export interface ProximityMatch {
  device1: string;
  device2: string;
  timestamp: Date;
  location: [number, number]; // [lat, lng]
  distance: number; // meters
  duration: number; // seconds both devices were in proximity
}

export interface LinkNode {
  id: string;
  label: string;
  type: 'device';
  entity_type: string;
  val: number; // Node size
  color: string;
  // Forensic flags data (optional, populated when queried)
  forensic_flags?: {
    unique_flags: string[];
    flag_counts: Record<string, number>;
    flag_percentages: Record<string, number>;
    total_signals: number;
    signals_with_flags: number;
  };
}

export interface LinkEdge {
  source: string;
  target: string;
  value: number; // Connection strength (number of proximity events)
  label: string;
  type: 'proximity' | 'dwell'; // proximity = device-device, dwell = device-location
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in meters
 */
function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Find all proximity matches within a given distance and time window
 */
export function findProximityMatches(
  dataPoints: PathfinderDataPoint[],
  maxDistance: number = 50, // meters
  timeWindow: number = 300 // seconds (5 minutes)
): ProximityMatch[] {
  const matches: ProximityMatch[] = [];
  
  // Sort by timestamp for efficient windowing
  const sortedPoints = [...dataPoints].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
  );


  // Group points by device for faster lookup
  const devicePoints = new Map<string, PathfinderDataPoint[]>();
  sortedPoints.forEach(point => {
    const deviceId = point.deviceId || point.studentId;
    if (!devicePoints.has(deviceId)) {
      devicePoints.set(deviceId, []);
    }
    devicePoints.get(deviceId)!.push(point);
  });

  const devices = Array.from(devicePoints.keys());

  // Compare each pair of devices
  for (let i = 0; i < devices.length; i++) {
    for (let j = i + 1; j < devices.length; j++) {
      const device1 = devices[i];
      const device2 = devices[j];
      const points1 = devicePoints.get(device1)!;
      const points2 = devicePoints.get(device2)!;

      // Find overlapping time windows
      let p2Index = 0;
      for (const p1 of points1) {
        // Move p2Index forward to time window
        while (
          p2Index < points2.length &&
          points2[p2Index].timestamp.getTime() <
            p1.timestamp.getTime() - timeWindow * 1000
        ) {
          p2Index++;
        }

        // Check all p2 points within time window
        for (let k = p2Index; k < points2.length; k++) {
          const p2 = points2[k];
          const timeDiff = Math.abs(
            p2.timestamp.getTime() - p1.timestamp.getTime()
          ) / 1000;

          if (timeDiff > timeWindow) break; // Moved past time window

          // Calculate distance (coordinates are internal format: [lat, lng])
          const distance = haversineDistance(
            p1.coordinates[0],  // lat
            p1.coordinates[1],  // lon
            p2.coordinates[0],  // lat
            p2.coordinates[1]   // lon
          );

          if (distance <= maxDistance) {
            matches.push({
              device1,
              device2,
              timestamp: p1.timestamp,
              location: [
                (p1.coordinates[0] + p2.coordinates[0]) / 2,  // avg lat
                (p1.coordinates[1] + p2.coordinates[1]) / 2,  // avg lng
              ],
              distance,
              duration: timeWindow - timeDiff,
            });
          }
        }
      }
    }
  }

  return matches;
}

/**
 * Generate network graph nodes and links from proximity matches
 */
export function generateLinkGraph(
  proximityMatches: ProximityMatch[],
): { nodes: LinkNode[]; links: LinkEdge[] } {
  // Count connections per device pair
  const connectionCounts = new Map<string, number>();
  const deviceSet = new Set<string>();

  proximityMatches.forEach(match => {
    const key = [match.device1, match.device2].sort().join('|');
    connectionCounts.set(key, (connectionCounts.get(key) || 0) + 1);
    deviceSet.add(match.device1);
    deviceSet.add(match.device2);
  });

  // Generate nodes
  const nodes: LinkNode[] = Array.from(deviceSet).map((deviceId, _) => {
    // Count how many connections this device has
    let connectionCount = 0;
    connectionCounts.forEach((count, key) => {
      if (key.includes(deviceId)) connectionCount += count;
    });

    // Generate color based on device ID hash
    const hue = (deviceId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360);
    const color = `hsl(${hue}, 70%, 60%)`;

    return {
      id: deviceId,
      label: deviceId,
      type: 'device' as const,
      entity_type: 'Device',
      val: Math.log(connectionCount + 1) * 3, // Logarithmic scaling for node size
      color,
    };
  });

  // Generate links
  const links: LinkEdge[] = [];
  connectionCounts.forEach((count, key) => {
    const [device1, device2] = key.split('|');
    links.push({
      source: device1,
      target: device2,
      value: count,
      label: `${count} co-location${count > 1 ? 's' : ''}`,
      type: 'proximity',
    });
  });


  return { nodes, links };
}

/**
 * Filter data points by date range
 */
export function filterByDateRange(
  dataPoints: PathfinderDataPoint[],
  startDate: Date,
  endDate: Date
): PathfinderDataPoint[] {
  return dataPoints.filter(
    point =>
      point.timestamp >= startDate && point.timestamp <= endDate
  );
}

/**
 * Represents a location cluster where multiple devices have been seen
 */
export interface LocationCluster {
  id: string;
  center: [number, number]; // [lat, lng]
  deviceVisits: Map<string, number>; // deviceId -> visit count
  totalVisits: number;
  label?: string; // Human-readable label (from reverse geocoding or metadata)
}

/**
 * Represents a shared location relationship between two devices
 */
export interface SharedLocationMatch {
  device1: string;
  device2: string;
  sharedLocations: number; // Number of distinct locations both devices visited
  totalOverlapVisits: number; // Sum of minimum visits at each shared location
  locations: Array<{
    id: string;
    center: [number, number];
    device1Visits: number;
    device2Visits: number;
    label?: string;
  }>;
}

/**
 * Result of shared location analysis including clusters
 */
export interface SharedLocationAnalysis {
  matches: SharedLocationMatch[];
  clusters: LocationCluster[];
}

/**
 * Generate a human-readable label for coordinates
 */
function generateLocationLabel(lat: number, lng: number, index: number): string {
  // Create a short readable label from coordinates
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `Location ${index + 1} (${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir})`;
}

/**
 * Find shared locations between devices
 * Groups all device locations into clusters, then finds which devices share clusters
 */
export function findSharedLocations(
  dataPoints: PathfinderDataPoint[],
  clusterRadius: number = 100 // meters - how close points need to be to be considered same location
): SharedLocationAnalysis {
  // Step 1: Cluster all points into location clusters
  const clusters: LocationCluster[] = [];
  
  // Coordinates are internal format: [lat, lng]
  dataPoints.forEach(point => {
    const deviceId = point.deviceId || point.studentId;
    const lat = point.coordinates[0];
    const lng = point.coordinates[1];
    
    // Try to get a place name from metadata
    const placeName = point.metadata?.placeName || point.metadata?.place_name || point.metadata?.address;
    
    // Find if this point belongs to an existing cluster
    // cluster.center is [lat, lng] for backward compatibility with geocoding
    let foundCluster = false;
    for (const cluster of clusters) {
      const distance = haversineDistance(
        lat, lng,
        cluster.center[0], cluster.center[1]  // cluster.center is [lat, lng]
      );
      
      if (distance <= clusterRadius) {
        // Add to existing cluster
        const currentVisits = cluster.deviceVisits.get(deviceId) || 0;
        cluster.deviceVisits.set(deviceId, currentVisits + 1);
        cluster.totalVisits++;
        // Update label if we found a better one
        if (placeName && !cluster.label) {
          cluster.label = placeName;
        }
        foundCluster = true;
        break;
      }
    }
    
    if (!foundCluster) {
      // Create new cluster - center is [lat, lng] for backward compatibility
      const newCluster: LocationCluster = {
        id: `loc-${clusters.length}`,
        center: [lat, lng],  // Keep as [lat, lng] for geocoding compatibility
        deviceVisits: new Map([[deviceId, 1]]),
        totalVisits: 1,
        label: placeName || generateLocationLabel(lat, lng, clusters.length)
      };
      clusters.push(newCluster);
    }
  });

  // Step 2: Find devices that share clusters
  const devicePairSharedLocations = new Map<string, SharedLocationMatch>();
  const allDevices = new Set<string>();
  
  dataPoints.forEach(point => {
    allDevices.add(point.deviceId || point.studentId);
  });
  
  const deviceList = Array.from(allDevices);
  
  // For each pair of devices, count shared locations
  for (let i = 0; i < deviceList.length; i++) {
    for (let j = i + 1; j < deviceList.length; j++) {
      const device1 = deviceList[i];
      const device2 = deviceList[j];
      const pairKey = [device1, device2].sort().join('|');
      
      const sharedLocations: SharedLocationMatch['locations'] = [];
      
      clusters.forEach(cluster => {
        const d1Visits = cluster.deviceVisits.get(device1) || 0;
        const d2Visits = cluster.deviceVisits.get(device2) || 0;
        
        if (d1Visits > 0 && d2Visits > 0) {
          sharedLocations.push({
            id: cluster.id,
            center: cluster.center,
            device1Visits: d1Visits,
            device2Visits: d2Visits,
            label: cluster.label
          });
        }
      });
      
      if (sharedLocations.length > 0) {
        devicePairSharedLocations.set(pairKey, {
          device1,
          device2,
          sharedLocations: sharedLocations.length,
          totalOverlapVisits: sharedLocations.reduce(
            (sum, loc) => sum + Math.min(loc.device1Visits, loc.device2Visits),
            0
          ),
          locations: sharedLocations
        });
      }
    }
  }
  
  return {
    matches: Array.from(devicePairSharedLocations.values()),
    clusters
  };
}

/**
 * Extended node type for location nodes
 */
export interface LocationNode {
  id: string;
  label: string;
  type: 'location';
  entity_type: string;
  val: number;
  color: string;
  coordinates: [number, number];
  visitCount: number;
  deviceCount: number;
  // Optional place data (from geocoding)
  place_name?: string;
  place_type?: string;
  place_address?: string;
  totalDuration?: number;
}

/**
 * Enrich location clusters with real place names from Mapbox geocoding
 * This is async and should be called after initial analysis
 */
export async function enrichLocationsWithPlaceNames(
  analysis: SharedLocationAnalysis,
  onProgress?: (completed: number, total: number) => void
): Promise<SharedLocationAnalysis> {
  const { clusters, matches } = analysis;
  
  console.log(`[enrichLocationsWithPlaceNames] Starting with ${clusters.length} clusters, ${matches.length} matches`);
  
  // Prepare coordinates for batch geocoding
  // Only geocode clusters that have multiple device visits (shared locations)
  const sharedClusterIds = new Set<string>();
  matches.forEach(match => {
    match.locations.forEach(loc => sharedClusterIds.add(loc.id));
  });
  
  console.log(`[enrichLocationsWithPlaceNames] Found ${sharedClusterIds.size} shared location IDs`);
  
  const coordsToGeocode = clusters
    .filter(cluster => sharedClusterIds.has(cluster.id))
    .map(cluster => ({
      id: cluster.id,
      lat: cluster.center[0],
      lng: cluster.center[1]
    }));

  console.log(`[enrichLocationsWithPlaceNames] Will geocode ${coordsToGeocode.length} locations`);

  if (coordsToGeocode.length === 0) {
    console.log(`[enrichLocationsWithPlaceNames] No locations to geocode, returning original analysis`);
    return analysis;
  }

  // Batch geocode all shared locations (with rich data including place_type)
  const geocodingResults = await batchReverseGeocodeRich(coordsToGeocode, onProgress);
  console.log(`[enrichLocationsWithPlaceNames] Got ${geocodingResults.size} geocoding results back`);

  // Update cluster labels and place_type
  // Count name occurrences to detect duplicates
  const nameCounts = new Map<string, number>();
  const nameCoords = new Map<string, Array<{ id: string; lat: number; lng: number }>>();
  
  coordsToGeocode.forEach(coord => {
    const result = geocodingResults.get(coord.id);
    if (result?.name) {
      const name = result.name;
      nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
      if (!nameCoords.has(name)) {
        nameCoords.set(name, []);
      }
      nameCoords.get(name)!.push({ id: coord.id, lat: coord.lat, lng: coord.lng });
    }
  });

  // Helper to get unique label (add coords if duplicate name)
  const getUniqueLabel = (_: string, name: string, lat: number, lng: number): string => {
    const count = nameCounts.get(name) || 0;
    if (count > 1) {
      // Multiple locations with same name - add coordinates
      return `${name} (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
    }
    return name;
  };

  // Update cluster labels - use name, add coords for duplicates
  const updatedClusters = clusters.map(cluster => {
    const result = geocodingResults.get(cluster.id);
    if (result) {
      const lat = cluster.center[0];
      const lng = cluster.center[1];
      const displayLabel = getUniqueLabel(cluster.id, result.name, lat, lng);
      return { 
        ...cluster, 
        label: displayLabel,
        place_type: result.place_type || 'location',
        place_address: result.address
      };
    }
    return cluster;
  });

  // Update match location labels - use name, add coords for duplicates
  const updatedMatches = matches.map(match => ({
    ...match,
    locations: match.locations.map(loc => {
      const result = geocodingResults.get(loc.id);
      if (result) {
        // Find the coordinates for this location
        const coordInfo = coordsToGeocode.find(c => c.id === loc.id);
        const lat = coordInfo?.lat || 0;
        const lng = coordInfo?.lng || 0;
        const displayLabel = getUniqueLabel(loc.id, result.name, lat, lng);
        return { ...loc, label: displayLabel, place_type: result.place_type };
      }
      return loc;
    })
  }));

  return {
    clusters: updatedClusters,
    matches: updatedMatches
  };
}

/**
 * Enrich dwell nodes in a graph with geocoded place names
 * This is async and should be called after generatePolActivityGraph
 */
export async function enrichDwellNodesWithPlaceNames(
  nodes: (LinkNode | LocationNode)[],
  onProgress?: (completed: number, total: number) => void
): Promise<(LinkNode | LocationNode)[]> {
  // Find dwell/location nodes that need geocoding (not already geocoded)
  const dwellNodes = nodes.filter(node => {
    if (node.type !== 'location') return false;
    const locNode = node as LocationNode;
    // Check if it's a dwell node without a real place name
    // (place_name looks like coordinates, generic site IDs, or is missing)
    const placeName = locNode.place_name || locNode.label || '';
    const needsGeocoding = 
      // Coordinate format: "37.7749, -122.4194" or "37.7749°N, 122.4194°W"
      /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(placeName) || 
      /^\d+\.\d+°[NS],\s*\d+\.\d+°[EW]$/.test(placeName) ||
      // Generic site IDs: "Site A", "Site B3", "dwell-site-123"
      /^Site\s+[A-Z]\d*$/i.test(placeName) ||
      /^dwell-site-\d+$/i.test(placeName) ||
      // Location format or empty
      placeName === '' ||
      placeName.startsWith('Location (') ||
      // Cluster IDs
      /^cluster-\d+$/.test(placeName);
    return needsGeocoding && locNode.coordinates;
  }) as LocationNode[];

  console.log(`[enrichDwellNodes] Found ${dwellNodes.length} dwell nodes needing geocoding out of ${nodes.length} total nodes`);

  if (dwellNodes.length === 0) {
    return nodes;
  }

  // Prepare coordinates for batch geocoding
  // Note: LocationNode.coordinates is [lat, lng] (set from cluster.center)
  const coordsToGeocode = dwellNodes.map(node => ({
    id: node.id,
    lat: node.coordinates![0], // Internal format: [lat, lng]
    lng: node.coordinates![1]
  }));

  // Batch geocode all dwell locations
  const geocodingResults = await batchReverseGeocodeRich(coordsToGeocode, onProgress);
  console.log(`[enrichDwellNodes] Got ${geocodingResults.size} geocoding results`);

  // Count name occurrences to detect duplicates
  const nameCounts = new Map<string, number>();
  coordsToGeocode.forEach(coord => {
    const result = geocodingResults.get(coord.id);
    if (result?.name) {
      nameCounts.set(result.name, (nameCounts.get(result.name) || 0) + 1);
    }
  });

  // Helper to get unique label (add coords if duplicate name)
  const getUniqueLabel = (name: string, lat: number, lng: number): string => {
    const count = nameCounts.get(name) || 0;
    if (count > 1) {
      // Multiple locations with same name - add coordinates
      return `${name} (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
    }
    return name;
  };

  // Update nodes with geocoded place names
  // Use name for label, add coords if duplicate names
  const updatedNodes = nodes.map(node => {
    if (node.type !== 'location') return node;
    
    const result = geocodingResults.get(node.id);
    if (result) {
      const locNode = node as LocationNode;
      const lat = locNode.coordinates![0]; // Internal format: [lat, lng]
      const lng = locNode.coordinates![1];
      const displayLabel = result.name 
        ? getUniqueLabel(result.name, lat, lng)
        : locNode.label;
      return {
        ...locNode,
        label: displayLabel,
        place_name: result.name || locNode.place_name,
        place_type: result.place_type || locNode.place_type,
        place_address: result.address
      };
    }
    return node;
  });

  return updatedNodes;
}

/**
 * Interface for DwellLocation (imported from dwellDetection.ts)
 */
interface DwellLocationInput {
  id: string | number;
  entity_id: string;
  device_type: string;
  latitude: number;
  longitude: number;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  point_count?: number;
  site_id?: string;
  site_name?: string;
}

/**
 * Generate PoL Activity graph that includes:
 * - All detected dwell locations (even from single devices)
 * - Shared location clusters between devices
 * - Device nodes for all devices with activity
 * - Links from devices to their dwell/visited locations
 */
export function generatePolActivityGraph(
  analysis: SharedLocationAnalysis,
  dwellLocations: DwellLocationInput[],
  minSharedLocations: number = 1,
  clusterRadius: number = 100
): { nodes: (LinkNode | LocationNode)[]; links: LinkEdge[] } {
  const { matches, clusters } = analysis;
  
  // Collect all devices that should be in the graph
  const deviceSet = new Set<string>();
  
  // Add devices from shared location matches
  matches.forEach(match => {
    if (match.sharedLocations >= minSharedLocations) {
      deviceSet.add(match.device1);
      deviceSet.add(match.device2);
    }
  });
  
  // Add devices from dwell locations
  dwellLocations.forEach(dwell => {
    deviceSet.add(dwell.entity_id);
  });
  
  // If no devices, return empty
  if (deviceSet.size === 0) {
    return { nodes: [], links: [] };
  }
  
  // Group dwells by site (cluster nearby dwells)
  const dwellSites = new Map<string, {
    id: string;
    center: [number, number]; // [lng, lat] GeoJSON format
    dwells: DwellLocationInput[];
    deviceVisits: Map<string, number>; // device -> visit count
    totalDuration: number;
    totalVisits: number;
    label?: string;
    place_type?: string;
  }>();
  
  dwellLocations.forEach(dwell => {
    const dwellCoord: [number, number] = [dwell.longitude, dwell.latitude];
    let foundSite: string | null = null;
    
    // Check if this dwell is close to an existing site
    dwellSites.forEach((site, siteId) => {
      const distance = haversineDistance(
        site.center[1], // lat
        site.center[0], // lng
        dwellCoord[1],  // lat
        dwellCoord[0]   // lng
      );
      if (distance <= clusterRadius) {
        foundSite = siteId;
      }
    });
    
    if (foundSite) {
      // Add to existing site
      const site = dwellSites.get(foundSite)!;
      site.dwells.push(dwell);
      const currentVisits = site.deviceVisits.get(dwell.entity_id) || 0;
      site.deviceVisits.set(dwell.entity_id, currentVisits + 1);
      site.totalDuration += dwell.duration_seconds;
      site.totalVisits += 1;
    } else {
      // Create new site
      const siteId = dwell.site_id || `dwell-site-${dwellSites.size + 1}`;
      dwellSites.set(siteId, {
        id: siteId,
        center: dwellCoord,
        dwells: [dwell],
        deviceVisits: new Map([[dwell.entity_id, 1]]),
        totalDuration: dwell.duration_seconds,
        totalVisits: 1,
        label: dwell.site_name,
      });
    }
  });
  
  // Collect location IDs from shared matches
  const sharedLocationIds = new Set<string>();
  matches.forEach(match => {
    if (match.sharedLocations >= minSharedLocations) {
      match.locations.forEach(loc => sharedLocationIds.add(loc.id));
    }
  });
  
  // Generate device nodes
  const deviceNodes: LinkNode[] = Array.from(deviceSet).map(deviceId => {
    // Count connections
    let connectionCount = 0;
    
    // Count shared location connections
    matches.forEach(match => {
      if (match.sharedLocations >= minSharedLocations) {
        if (match.device1 === deviceId || match.device2 === deviceId) {
          connectionCount += match.sharedLocations;
        }
      }
    });
    
    // Count dwell connections
    dwellSites.forEach(site => {
      if (site.deviceVisits.has(deviceId)) {
        connectionCount += site.deviceVisits.get(deviceId)!;
      }
    });

    const hue = (deviceId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360);
    const color = `hsl(${hue}, 70%, 60%)`;

    return {
      id: deviceId,
      label: deviceId,
      type: 'device' as const,
      entity_type: 'Device',
      val: Math.log(connectionCount + 1) * 3,
      color,
    };
  });
  
  // Generate location nodes from shared location clusters
  const sharedLocationNodes: LocationNode[] = clusters
    .filter(cluster => sharedLocationIds.has(cluster.id))
    .map(cluster => ({
      id: cluster.id,
      label: cluster.label || `Location ${cluster.id}`,
      type: 'location' as const,
      entity_type: 'Shared Location',
      val: Math.log(cluster.totalVisits + 1) * 2,
      color: '#00ff88', // Green for shared locations
      coordinates: cluster.center,
      visitCount: cluster.totalVisits,
      deviceCount: cluster.deviceVisits.size,
      place_name: cluster.label || `Location ${cluster.id}`,
      place_type: (cluster as any).place_type || 'shared',
    }));
  
  // Generate location nodes from dwell sites
  const dwellSiteNodes: LocationNode[] = Array.from(dwellSites.values())
    // Only include dwell sites not already covered by shared locations
    .filter(site => {
      // Check if any shared location cluster is near this dwell site
      for (const cluster of clusters) {
        if (sharedLocationIds.has(cluster.id)) {
          const distance = haversineDistance(
            cluster.center[1], // lat
            cluster.center[0], // lng
            site.center[1],    // lat
            site.center[0]     // lng
          );
          if (distance <= clusterRadius) {
            return false; // Skip - already covered by shared location
          }
        }
      }
      return true;
    })
    .map(site => ({
      id: site.id,
      label: site.label || formatCoordLabel(site.center[1], site.center[0]),
      type: 'location' as const,
      entity_type: site.deviceVisits.size > 1 ? 'Shared Dwell' : 'Dwell Site',
      val: Math.log(site.totalVisits + 1) * 2,
      color: site.deviceVisits.size > 1 ? '#ffaa00' : '#ff0066', // Orange for shared dwells, pink for single-device dwells
      coordinates: site.center,
      visitCount: site.totalVisits,
      deviceCount: site.deviceVisits.size,
      place_name: site.label || formatCoordLabel(site.center[1], site.center[0]),
      place_type: 'dwell',
      totalDuration: site.totalDuration,
    }));
  
  // Generate links
  const links: LinkEdge[] = [];
  
  // Links from devices to shared location clusters
  clusters
    .filter(cluster => sharedLocationIds.has(cluster.id))
    .forEach(cluster => {
      cluster.deviceVisits.forEach((visits, deviceId) => {
        if (deviceSet.has(deviceId)) {
          links.push({
            source: deviceId,
            target: cluster.id,
            value: visits,
            label: `${visits} visit${visits > 1 ? 's' : ''}`,
            type: 'dwell' as const,
          });
        }
      });
    });
  
  // Links from devices to dwell sites (only for sites not covered by shared locations)
  dwellSiteNodes.forEach(siteNode => {
    const site = dwellSites.get(siteNode.id);
    if (site) {
      site.deviceVisits.forEach((visits, deviceId) => {
        if (deviceSet.has(deviceId)) {
          // Calculate total duration for this device at this site
          const deviceDwells = site.dwells.filter(d => d.entity_id === deviceId);
          const totalDuration = deviceDwells.reduce((sum, d) => sum + d.duration_seconds, 0);
          const durationStr = formatDuration(totalDuration);
          
          links.push({
            source: deviceId,
            target: siteNode.id,
            value: visits,
            label: `${visits} dwell${visits > 1 ? 's' : ''} (${durationStr})`,
            type: 'dwell' as const,
          });
        }
      });
    }
  });
  
  return {
    nodes: [...deviceNodes, ...sharedLocationNodes, ...dwellSiteNodes],
    links
  };
}

/**
 * Format coordinates as a label
 */
function formatCoordLabel(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

/**
 * Format duration in seconds to human-readable string
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

